/*
 * Typing, both ends of it in the chat module: the frame this person's keystrokes send, and the line
 * "Bea is typing …" between a list of messages and the box under it (A-09 #10, UX Q7).
 *
 * Both ends are here because they are one decision, and the decision is per *box*, not per screen.
 * The conversation's own line happens in direct conversations alone: in a channel of seven people a
 * typing line is noise that never stops, and in a conversation with one person it is the thing that
 * says somebody is there — which is the whole reason the signal exists. A **thread** is the other
 * room where it earns its place, whatever the conversation around it is: a thread is a handful of
 * people answering each other, and "Bea is typing …" under the reply box is the same fact there as
 * it is in a direct message (X-A1 A5). The conversation's line refuses to draw itself under a
 * channel even so, because an older client's frame must not be able to put one there.
 *
 * The composer core knows nothing about conversations; it calls the signal installed here once per
 * interval per target while its box has something in it, and this is where "is this target worth a
 * frame, and which box is it" is answered. One handler covers both boxes: a thread pane lives at
 * `?thread=` on the conversation's own route, so the conversation is on screen whenever the pane is,
 * and two installers would overwrite each other's single slot.
 *
 * What the line says — who, for how long, coalesced how — is the people area's, the same in every
 * room that has a line (core/people/typing.ts). Its element is always in the DOM while the box it
 * belongs to is, empty when nobody is writing: a live region has to exist before the change it is
 * to announce (WCAG 4.1.3), and a line that appears and disappears would push the messages up and
 * down under the reader's eyes.
 */

import { useEffect } from "react"
import { setTypingSignal } from "../../../core/composer/useComposerCore.ts"
import { useTypingSentence } from "../../../core/people/typing.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { chatDeps } from "../deps.tsx"
import { chatSlice } from "../state/selectors.ts"
import { KindDm, KindGroupDm } from "../wire/chat.gen.ts"

declare module "../deps.tsx" {
	interface ChatDeps {
		/**
		 * `chat.typing` (protocol.md §2.5.4), fire and forget. It is declared beside the code that
		 * needs it, the way `reactions/deps.ts` declares its two: an interface is open, and the boot
		 * binds every chat call in one place (`modules/chat/bind.ts`).
		 *
		 * `threadRootId` is which box the keystrokes are in, not a second action: the server
		 * authorises the conversation either way, because a thread is inside it.
		 *
		 * Unbound it is simply not sent. Nothing on screen depends on the frame having gone — a line
		 * is drawn from what arrives, never from what this browser sent.
		 */
		typing?: ((input: { conv: number; threadRootId?: number }) => Promise<void>) | undefined
	}
}

/** The kinds a conversation's own typing line belongs under: the ones a person is talking *to*. */
function isDirectKind(kind: string | undefined): boolean {
	return kind === KindDm || kind === KindGroupDm
}

/**
 * Sends this person's typing frames for the boxes of one conversation: its own, while it is a direct
 * conversation, and the reply box of whatever thread is open beside it.
 *
 * The core throttles (one call per `typingIntervalMs` per target); this decides *whether*. An
 * in-place edit arrives under `e:<id>` and is nobody's business but the editor's.
 */
export function useTypingSignal(conv: ConversationId): void {
	const kind = useModel((model) => model.conversations[conv]?.meta?.kind)
	const direct = isDirectKind(kind)
	useEffect(() => {
		const own = `c:${conv}`
		const threadPrefix = `${own}:t:`
		setTypingSignal((targetKey) => {
			const root = targetKey.startsWith(threadPrefix)
				? Number(targetKey.slice(threadPrefix.length))
				: 0
			if (root === 0 && !(direct && targetKey === own)) return
			void chatDeps()
				.typing?.(root === 0 ? { conv } : { conv, threadRootId: root })
				?.catch(() => undefined)
		})
		return () => setTypingSignal(undefined)
	}, [direct, conv])
}

export interface TypingLineProps {
	conv: ConversationId
}

export function TypingLine({ conv }: TypingLineProps) {
	const typing = useModel((model) => chatSlice(model).typing[conv])
	const kind = useModel((model) => model.conversations[conv]?.meta?.kind)
	const sentence = useTypingSentence(typing)

	if (!isDirectKind(kind)) return null
	return (
		<p className="tb-typing" role="status">
			{sentence}
		</p>
	)
}

export interface ThreadTypingLineProps {
	rootId: number
}

/**
 * The same line under a thread's reply box. It is keyed by the root and not by the conversation, so
 * that somebody writing in the channel does not appear to be writing in the thread or the other way
 * round — which is what the payload's `threadRootId` is for.
 */
export function ThreadTypingLine({ rootId }: ThreadTypingLineProps) {
	const typing = useModel((model) => chatSlice(model).threadTyping[rootId])
	const sentence = useTypingSentence(typing)
	return (
		<p className="tb-typing" role="status">
			{sentence}
		</p>
	)
}
